import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { Button, Input } from '@ai-tutor/ui';
import { Link as LinkIcon, Plus, Search } from 'lucide-react';
import {
  useExtras,
  useDeleteExtrasAttachment,
} from '@/hooks/lesson/useOnDemandContent';
import { useClassifiedChat } from '@/hooks/chat/useClassifiedChat';
import type { ExtrasAttachment, ClassifiedEntry } from '@/lib/api-client';
import { ExtrasSkeleton } from '../skeletons/PanelSkeletons';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { DeepDiveCard } from '../extras/DeepDiveCard';
import { AttachmentCard } from '../extras/AttachmentCard';
import { AddAttachmentModal } from '../extras/AddAttachmentModal';

interface ExtrasManagerProps {
  lessonId: string;
}

/**
 * Manually-curated Extras tab. The user attaches either:
 *  - another lesson from their library (opens the lesson on click), or
 *  - an external URL (opens in a new tab on click)
 *
 * Replaces the LLM-generated references/related_topics flow - the
 * reliability problem there was hallucinated URLs. Manual attachments
 * are always real.
 */
export function ExtrasManager({ lessonId }: ExtrasManagerProps) {
  const navigate = useNavigate();

  // Mount/unmount activity tracking for the Extras sub-tab. Shares the
  // 'reference' content_type with the other Reference sub-tabs - the
  // backend's interval-union handles overlapping rows correctly.
  useActivityTracking({ contentType: 'reference', lessonId });

  const { attachments, isLoading } = useExtras(lessonId);
  const deleteAttachment = useDeleteExtrasAttachment(lessonId);
  // Deep-dives surface as promotion candidates above the manual list.
  // Same hook the Appendix Doubts tab uses → shared cache entry.
  const { deepDives } = useClassifiedChat(lessonId);
  const [isAddOpen, setAddOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handlePromoteDeepDive = (entry: ClassifiedEntry) => {
    // Navigate to the new-lesson page with the question pre-filled
    // and the source linkage encoded as URL params. The generator
    // page reads these, includes them in the POST body, and the
    // backend (a) marks this chat message as promoted and (b)
    // auto-attaches the new lesson to this lesson's Extras list.
    const params = new URLSearchParams({
      topic: entry.question,
      from_deep_dive_lesson: lessonId,
      from_deep_dive_message: entry.id,
    });
    navigate(`/?${params.toString()}`);
  };

  const handleOpen = (a: ExtrasAttachment) => {
    if (a.kind === 'lesson' && a.lesson_id) {
      navigate(`/my-learning/${a.lesson_id}`);
    } else if (a.kind === 'link' && a.url) {
      window.open(a.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDelete = async (e: React.MouseEvent, attachmentId: string) => {
    e.stopPropagation();
    try {
      await deleteAttachment.mutateAsync(attachmentId);
    } catch (err: any) {
      notify.error(err, "Couldn't remove attachment");
    }
  };

  // Local search across title / description / url. Mirrors the
  // glossary tab's filter behavior so the two surfaces feel uniform.
  const filteredAttachments = attachments.filter(a => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      a.title.toLowerCase().includes(q) ||
      (a.description?.toLowerCase().includes(q) ?? false) ||
      (a.url?.toLowerCase().includes(q) ?? false)
    );
  });

  // Loading the curated attachments list. Use the shared ExtrasSkeleton
  // (PuffLoader + FolderOpen icon) in fetch-mode so the copy reads
  // "Loading Extras" / "Fetching your extras." - consistent with how
  // the other panels announce a network-only wait vs. a pipeline run.
  if (isLoading) {
    return <ExtrasSkeleton mode='fetching' />;
  }

  // Empty state - same visual rhythm as the OnDemandWrapper "not_started"
  // surface used elsewhere (Quiz, Highlights, Flashcards) so all the
  // lesson-tab empties look like one design.
  //
  // Special case: even when the manual attachment list is empty, we
  // still want to surface deep-dive suggestions if any exist - they're
  // useful by themselves. Fall through to the populated path in that
  // case so the suggestion section can render.
  if (attachments.length === 0 && deepDives.length === 0) {
    return (
      <div className='flex h-full animate-fade-in flex-col items-center justify-center px-4 py-16'>
        <div className='mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-border/60'>
          <LinkIcon className='h-7 w-7 text-brand-500/80 dark:text-primary' />
        </div>
        <h3 className='mb-2 text-lg font-medium text-foreground'>Extras</h3>
        <p className='mb-6 max-w-sm text-center text-sm text-muted-foreground'>
          Attach lessons from your library or paste external URLs to keep every
          reference for this lesson in one place.
        </p>
        <Button onClick={() => setAddOpen(true)} className='h-11 sm:h-9'>
          <Plus className='mr-2 h-4 w-4' />
          Add attachment
        </Button>

        <AddAttachmentModal
          isOpen={isAddOpen}
          onClose={() => setAddOpen(false)}
          lessonId={lessonId}
          existingAttachments={attachments}
        />
      </div>
    );
  }

  // Populated list - search row mirrors the Glossary tab; the dotted
  // "Add new" card sits on top of the list and replaces the standalone
  // header button.
  return (
    <div className='flex h-full animate-fade-in flex-col'>
      {/* Search, and Add beside it: the full-width dashed "Add attachment"
          box this replaces took a whole row above the list. */}
      <div className='p-4'>
        <div className='flex items-center gap-2'>
          <div className='relative flex-1'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground' />
            <Input
              placeholder='Search attachments...'
              aria-label='Search attachments'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='bg-transparent pl-10 text-foreground ![box-shadow:none]'
            />
          </div>
          <Button
            type='button'
            variant='outline'
            onClick={() => setAddOpen(true)}
            aria-label='Add attachment'
            title='Add attachment'
            className='h-11 w-11 flex-shrink-0 bg-transparent p-0 md:h-9 md:w-9'
          >
            <Plus className='h-4 w-4' aria-hidden='true' />
          </Button>
        </div>
      </div>

      <div className='flex-1 overflow-y-auto p-4 pt-0'>
        {/* Below `sm` the attachments are hairline-divided rows
            (`AttachmentCard`), so the boxes' gap goes too. */}
        <div className='space-y-3 max-sm:space-y-0'>
          {filteredAttachments.length === 0 && attachments.length > 0 ? (
            <div className='py-8 text-center text-sm text-muted-foreground'>
              {`No attachments match "${searchTerm}".`}
            </div>
          ) : (
            filteredAttachments.map(a => (
              <AttachmentCard
                key={a.attachment_id}
                attachment={a}
                onOpen={() => handleOpen(a)}
                onDelete={e => handleDelete(e, a.attachment_id)}
                isDeleting={
                  deleteAttachment.isPending &&
                  deleteAttachment.variables === a.attachment_id
                }
              />
            ))
          )}

          {/* Suggested deep-dives - chat turns the classifier flagged as
              "deep-dive" become potential new lessons. Click "Make a
              lesson" to navigate to the generator pre-filled with the
              question; on success the deep-dive disappears (backend
              filters it out via promoted_lesson_id) and the new lesson
              card lands in this same list above. */}
          {deepDives.length > 0 && (
            <section aria-labelledby='extras-go-deeper' className='pt-5'>
              <h3
                id='extras-go-deeper'
                className='text-sm font-semibold text-foreground'
              >
                Go deeper
                <span className='ml-2 font-mono text-xs font-normal text-muted-foreground'>
                  {deepDives.length}
                </span>
              </h3>
              <p className='mb-3 mt-0.5 text-xs text-muted-foreground'>
                Questions from your chats that could be a lesson of their own.
              </p>
              <div className='space-y-3'>
                {deepDives.map(entry => (
                  <DeepDiveCard
                    key={entry.id}
                    entry={entry}
                    onMakeLesson={handlePromoteDeepDive}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      <AddAttachmentModal
        isOpen={isAddOpen}
        onClose={() => setAddOpen(false)}
        lessonId={lessonId}
        existingAttachments={attachments}
      />
    </div>
  );
}
